/**
 * Competence Map Store
 *
 * Zustand store for the learner's per-competence mastery map
 * (GET /api/grammar/competence-map). States are computed server-side —
 * this store never re-derives thresholds; it only caches the response.
 *
 * Consumed by CompetenceMapScreen (full map) and the Practice tab's
 * CompetenceMapSummaryCard (summary counts). Both load on focus; the card
 * silently keeps stale data / falls back to a neutral CTA on failure.
 *
 * Deliberately the same shape as `soundMapStore` — the two maps are siblings
 * and diverging here would make every consumer of one surprising to whoever
 * next reads the other.
 *
 * @see docs/specs/mobile/competence-map.mobile.md
 * @see docs/specs/api/competence-map.api.md
 */

import { create } from 'zustand';
import { api } from '../services/api';
import { createLogger } from '../utils/logger';
import type { StoreErrorInfo } from '../generated/api/endpoint-errors';
import type { CompetenceMapResponse } from '../generated/api/http-contracts';

const log = createLogger('CompetenceMapStore');

interface CompetenceMapState {
  /** Last successfully fetched map — kept on refresh failure. */
  data: CompetenceMapResponse | null;
  loading: boolean;
  errorInfo: StoreErrorInfo | null;

  /** Fetch the competence map. No-ops while a fetch is already in flight. */
  loadCompetenceMap: () => Promise<void>;
  clearError: () => void;
}

export const useCompetenceMapStore = create<CompetenceMapState>((set, get) => ({
  data: null,
  loading: false,
  errorInfo: null,

  loadCompetenceMap: async () => {
    if (get().loading) return;
    set({ loading: true, errorInfo: null });
    const result = await api.getCompetenceMap();
    if (result.ok) {
      set({ data: result.data, loading: false });
    } else {
      // Keep any previously loaded data — a failed refresh is non-fatal for
      // consumers that already have a map to show.
      log.warn('Failed to load competence map', { kind: result.kind });
      set({ loading: false, errorInfo: result });
    }
  },

  clearError: () => set({ errorInfo: null }),
}));

// ─── Selectors (individual — CHAT_PERFORMANCE.md rule) ──────────────

export const selectCompetenceMapData = (s: CompetenceMapState) => s.data;
export const selectCompetenceMapLoading = (s: CompetenceMapState) => s.loading;
export const selectCompetenceMapErrorInfo = (s: CompetenceMapState) => s.errorInfo;
export const selectLoadCompetenceMap = (s: CompetenceMapState) => s.loadCompetenceMap;
export const selectClearCompetenceMapError = (s: CompetenceMapState) => s.clearError;
